import { NOMBRE_ROL, type RolGlobal } from "@gestiondoc/shared";
import { useQuery } from "@tanstack/react-query";
import { useRouteContext } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { authClient, queryClient, useTRPC } from "../api.ts";
import { Encabezado } from "../componentes/Encabezado.tsx";
import { ActivarSegundoFactor } from "../componentes/SegundoFactor.tsx";
import { Aviso, Boton, Campo, CLASE_ENTRADA, Dato, Pildora, Tarjeta } from "../componentes/ui.tsx";

/**
 * "Mi cuenta": quién soy según el administrador (nada se edita aquí: el nombre va a las firmas) y el
 * segundo factor (fase 6), que cada persona activa o cambia con su contraseña. Es obligatorio
 * para entrar desde fuera de la red; dentro de la oficina no se pide.
 */
export function PaginaCuenta() {
  const { perfil: inicial } = useRouteContext({ from: "/app" });
  const trpc = useTRPC();
  const { data: perfil = inicial } = useQuery({
    ...trpc.yo.perfil.queryOptions(),
    initialData: inicial,
  });
  const u = perfil.usuario;
  const recargar = () => queryClient.invalidateQueries({ queryKey: trpc.yo.perfil.queryKey() });

  return (
    <div className="max-w-[760px]">
      <Encabezado overline="Cuenta" titulo="Mi cuenta" />
      <div className="space-y-4">
        <Tarjeta
          titulo="Mis datos"
          ayuda="Los da de alta y los cambia un administrador: el nombre y el puesto van a las firmas."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Dato etiqueta="Nombre">{u.nombre}</Dato>
            <Dato etiqueta="Correo">{u.correo}</Dato>
            <Dato etiqueta="Puesto">{u.puesto || "—"}</Dato>
            <Dato etiqueta="Rol">{NOMBRE_ROL[u.rolGlobal as RolGlobal] ?? u.rolGlobal}</Dato>
            <Dato etiqueta="Acceso desde fuera de la red">
              {u.accesoExterno ? "Habilitado" : "No habilitado"}
            </Dato>
          </div>
        </Tarjeta>

        <Tarjeta
          titulo="Segundo factor"
          ayuda="Un código de tu celular además de la contraseña. Se pide al entrar desde fuera de la red; en la oficina no."
          {...(perfil.segundoFactor.activado ? { etiqueta: "Activado" } : {})}
        >
          {perfil.segundoFactor.activado ? (
            <Desactivar onDesactivado={recargar} />
          ) : (
            <>
              {u.accesoExterno && (
                <Aviso tono="ambar" className="mb-4">
                  Tu cuenta puede entrar desde fuera de la red: actívalo ahora, desde la oficina,
                  para no quedarte afuera el día que lo necesites.
                </Aviso>
              )}
              <ActivarSegundoFactor onActivado={recargar} />
            </>
          )}
        </Tarjeta>
      </div>
    </div>
  );
}

/** Cambiar de celular: se desactiva con la contraseña y se vuelve a activar con el nuevo. */
function Desactivar({ onDesactivado }: { onDesactivado: () => void }) {
  const [abierto, setAbierto] = useState(false);
  const [contrasena, setContrasena] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (!contrasena) {
      setError("Confirma con tu contraseña");
      return;
    }
    setError(null);
    setEnviando(true);
    const r = await authClient.twoFactor.disable({ password: contrasena });
    setEnviando(false);
    setContrasena("");
    if (r.error) {
      setError(
        r.error.status === 400
          ? "La contraseña no es correcta"
          : (r.error.message ?? "No se pudo desactivar"),
      );
      return;
    }
    setAbierto(false);
    onDesactivado();
  }

  if (!abierto) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <Pildora tono="bg-verde-fondo text-verde">Aplicación de autenticación activa</Pildora>
        <Boton variante="secundario" onClick={() => setAbierto(true)}>
          Cambiar de celular
        </Boton>
      </div>
    );
  }
  return (
    <form onSubmit={enviar} className="space-y-3">
      <p className="text-[12.5px] text-texto-3">
        Desactivarlo borra la clave actual y los códigos de respaldo; después lo activas de nuevo
        con el otro celular. Si tu cuenta entra desde fuera de la red, hazlo desde la oficina.
      </p>
      <Campo etiqueta="Contraseña">
        <input
          type="password"
          autoComplete="current-password"
          value={contrasena}
          onChange={(e) => setContrasena(e.target.value)}
          className={CLASE_ENTRADA}
        />
      </Campo>
      {error && <Aviso>{error}</Aviso>}
      <div className="flex gap-2">
        <Boton type="submit" variante="peligro" disabled={enviando}>
          {enviando ? "Desactivando…" : "Desactivar el segundo factor"}
        </Boton>
        <Boton type="button" variante="secundario" onClick={() => setAbierto(false)}>
          Cancelar
        </Boton>
      </div>
    </form>
  );
}
